import { InvoiceDetailDoc, InvoiceSendChannel } from '@gooduncles/gu-app-schema';
import { Alert, Button, Modal, Select, Space, Table, Tag, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { chunk, uniq } from 'lodash-es';
import { FC, useEffect, useMemo, useState } from 'react';

import { errorObjectToString, formatNumber } from 'src/utils/common';
import { batchEnd, batchStart, getStores, updateStore } from 'src/utils/firebase/firebase-short-cut';

export interface SendInvoiceSelection {
  /** 푸시로 보낼 행 (push | both) */
  pushRows: InvoiceDetailDoc[];
  /** 알림톡으로 보낼 행 (alimtalk | both) */
  alimtalkRows: InvoiceDetailDoc[];
}

/** Firestore 'in' 쿼리 한 번에 넣을 수 있는 최대 개수 */
const FIRESTORE_IN_QUERY_LIMIT = 30;

/** 알림톡 발송 가능한 최소 수신번호 길이 (기존 sendInvoiceDetails 기준) */
const MIN_TEL_LENGTH = 8;

interface SendInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  selectedRows: InvoiceDetailDoc[];
  /** storeId → v2 전환(푸시 가능) 여부 */
  conversionMap: Record<string, boolean>;
  loading: boolean;
  onSend: (selection: SendInvoiceSelection) => void;
}

interface SendInvoiceRow {
  key: string;
  row: InvoiceDetailDoc;
  storeName: string;
  amount: number;
  isV2: boolean;
  hasValidTel: boolean;
  /** 선택 가능한 채널 옵션 */
  options: InvoiceSendChannel[];
  /** 발송 가능 여부 (v1인데 수신번호 없으면 불가) */
  sendable: boolean;
}

const channelLabel: Record<InvoiceSendChannel, string> = {
  push: '푸시',
  alimtalk: '알림톡',
  both: '둘 다',
};

/** 전환 상태·수신번호로 행의 선택 가능 채널과 기본값을 정한다. */
const buildOptions = (isV2: boolean, hasValidTel: boolean): InvoiceSendChannel[] => {
  if (isV2) {
    // 푸시는 storeId 채널이라 항상 가능. 알림톡/둘 다는 수신번호가 있어야 가능.
    return hasValidTel ? ['push', 'alimtalk', 'both'] : ['push'];
  }
  return hasValidTel ? ['alimtalk'] : [];
};

const SendInvoiceModal: FC<SendInvoiceModalProps> = ({
  open,
  onClose,
  selectedRows,
  conversionMap,
  loading,
  onSend,
}) => {
  const rows = useMemo<SendInvoiceRow[]>(
    () =>
      selectedRows.map((row) => {
        const isV2 = !!conversionMap[row.storeId];
        const hasValidTel = row.recipientInfo.userTel.length > MIN_TEL_LENGTH;
        const options = buildOptions(isV2, hasValidTel);
        return {
          key: row._id,
          row,
          storeName: row.recipientInfo.name,
          amount: row.invoiceAmount,
          isV2,
          hasValidTel,
          options,
          sendable: options.length > 0,
        };
      }),
    [selectedRows, conversionMap],
  );

  // storeId → store에 저장된 직전 선택. 저장 시 변경분만 쓰기 위해 들고 있는다.
  const [savedChannels, setSavedChannels] = useState<Record<string, InvoiceSendChannel>>({});
  // rowId → 선택한 채널. 모달을 열 때마다 매장별 직전 선택(없으면 v2→푸시, v1→알림톡)으로 초기화한다.
  const [choiceById, setChoiceById] = useState<Record<string, InvoiceSendChannel>>({});
  // 조회가 끝나기 전에 발송하면 저장된 채널이 아닌 값으로 나갈 수 있어 그동안 발송을 막는다.
  const [channelsLoading, setChannelsLoading] = useState(true);
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setChannelsLoading(true);
    (async () => {
      try {
        const storeIds = uniq(rows.map((row) => row.row.storeId));
        const storeChunks = await Promise.all(
          chunk(storeIds, FIRESTORE_IN_QUERY_LIMIT).map((ids) => getStores([['_id', 'in', ids]])),
        );
        if (cancelled) {
          return;
        }
        const saved = storeChunks.flat().reduce<Record<string, InvoiceSendChannel>>(
          (acc, store) => (store.invoiceSendChannel ? { ...acc, [store._id]: store.invoiceSendChannel } : acc),
          {},
        );
        const next: Record<string, InvoiceSendChannel> = {};
        rows.forEach((row) => {
          if (row.sendable) {
            const lastChoice = saved[row.row.storeId];
            next[row.key] = lastChoice && row.options.includes(lastChoice) ? lastChoice : row.options[0];
          }
        });
        setSavedChannels(saved);
        setChoiceById(next);
      } catch (error) {
        console.error(error);
        notification.error({
          message: '매장별 발송 채널을 불러오지 못했습니다.',
          description: errorObjectToString(error),
        });
      } finally {
        if (!cancelled) {
          setChannelsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, rows]);

  const pushCount = rows.filter((row) => {
    const choice = choiceById[row.key];
    return choice === 'push' || choice === 'both';
  }).length;
  const alimtalkCount = rows.filter((row) => {
    const choice = choiceById[row.key];
    return choice === 'alimtalk' || choice === 'both';
  }).length;
  const unsendableCount = rows.filter((row) => !row.sendable).length;

  const handleSend = async () => {
    // 다음 발송의 기본값으로 쓰도록 바뀐 매장만 store에 남긴다. 실패해도 발송은 진행한다.
    // ponytail: batch 한 번(최대 500건). 한 번에 그보다 많은 매장의 채널을 바꾸면 나눠서 커밋해야 한다.
    const changedRows = rows.filter(
      (row) => choiceById[row.key] && choiceById[row.key] !== savedChannels[row.row.storeId],
    );
    if (changedRows.length > 0) {
      try {
        batchStart();
        for (const row of changedRows) {
          await updateStore(row.row.storeId, { invoiceSendChannel: choiceById[row.key] }, { bBatch: true });
        }
        await batchEnd();
      } catch (error) {
        console.error(error);
      }
    }

    const pushRows = rows
      .filter((row) => choiceById[row.key] === 'push' || choiceById[row.key] === 'both')
      .map((row) => row.row);
    const alimtalkRows = rows
      .filter((row) => choiceById[row.key] === 'alimtalk' || choiceById[row.key] === 'both')
      .map((row) => row.row);
    onSend({ pushRows, alimtalkRows });
  };

  const columns: ColumnsType<SendInvoiceRow> = [
    {
      title: '청구대상',
      dataIndex: 'storeName',
      key: 'storeName',
    },
    {
      title: '청구금액',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (value: number) => `${formatNumber(value)}원`,
    },
    {
      title: '전환',
      key: 'isV2',
      width: 90,
      render: (_, record) => (record.isV2 ? <Tag color='green'>v2</Tag> : <Tag color='orange'>v1</Tag>),
    },
    {
      title: '채널',
      key: 'channel',
      width: 160,
      render: (_, record) =>
        record.sendable ? (
          <Select<InvoiceSendChannel>
            value={choiceById[record.key]}
            style={{ width: 130 }}
            onChange={(value) => setChoiceById((prev) => ({ ...prev, [record.key]: value }))}
            options={record.options.map((option) => ({ value: option, label: channelLabel[option] }))}
          />
        ) : (
          <Tag color='red'>발송불가(번호없음)</Tag>
        ),
    },
  ];

  return (
    <Modal title='청구서 발송' open={open} onCancel={onClose} footer={null} width={720}>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <Alert
          type='info'
          showIcon
          message={`총 ${selectedRows.length}건 — 푸시 ${pushCount} / 알림톡 ${alimtalkCount}`}
          description={
            unsendableCount > 0
              ? `수신번호가 없고 푸시도 불가한 매장 ${unsendableCount}건은 발송에서 제외됩니다.`
              : undefined
          }
        />
        <Table
          columns={columns}
          dataSource={rows}
          pagination={false}
          size='small'
          scroll={{ y: 360 }}
          loading={channelsLoading}
        />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>취소</Button>
          <Button
            type='primary'
            onClick={handleSend}
            loading={loading || channelsLoading}
            disabled={channelsLoading || (pushCount === 0 && alimtalkCount === 0)}
          >
            {channelsLoading ? '채널 불러오는 중' : `발송 (푸시 ${pushCount} / 알림톡 ${alimtalkCount})`}
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default SendInvoiceModal;
